import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { useMemo, useState } from "react";
import { useLogos, type Logo } from "@/hooks/useLogos";
import { testimonialsTranslations } from "@/utils/translations/testimonials";
import { getOptimizedImageUrl } from "@/utils/imageUtils";
import { SectionHeading } from "./demo-shared";

interface DemoLogosProps {
  language: string;
}

type Filter = "all" | "clients" | "government" | "partners";

const INITIAL_COUNT = 12;

interface WallLogo extends Logo {
  category: Exclude<Filter, "all">;
}

const DemoLogos = ({ language }: DemoLogosProps) => {
  const isKo = language === "KO";
  const { clientLogos, governmentLogos, partnerLogos, isLoading, error } = useLogos();
  const translations =
    testimonialsTranslations[language as keyof typeof testimonialsTranslations] || testimonialsTranslations.EN;

  const [filter, setFilter] = useState<Filter>("all");
  const [expanded, setExpanded] = useState(false);

  const allLogos = useMemo<WallLogo[]>(
    () => [
      ...governmentLogos.map((logo) => ({ ...logo, category: "government" as const })),
      ...clientLogos.map((logo) => ({ ...logo, category: "clients" as const })),
      ...partnerLogos.map((logo) => ({ ...logo, category: "partners" as const })),
    ],
    [governmentLogos, clientLogos, partnerLogos],
  );

  const filtered = filter === "all" ? allLogos : allLogos.filter((logo) => logo.category === filter);
  const visible = expanded ? filtered : filtered.slice(0, INITIAL_COUNT);
  const hasMore = filtered.length > INITIAL_COUNT;

  const filters: { key: Filter; label: string; count: number }[] = [
    { key: "all", label: isKo ? "전체" : "All", count: allLogos.length },
    { key: "clients", label: translations.tabs.clients, count: clientLogos.length },
    { key: "government", label: translations.tabs.government, count: governmentLogos.length },
    { key: "partners", label: translations.tabs.partners, count: partnerLogos.length },
  ];

  const categoryLabel: Record<Exclude<Filter, "all">, string> = {
    clients: translations.tabs.clients,
    government: translations.tabs.government,
    partners: translations.tabs.partners,
  };

  if (error) return null;

  return (
    <section className="relative bg-[#FAFAF8] py-24 md:py-32">
      <div className="container mx-auto max-w-6xl px-4">
        <SectionHeading
          no="04"
          eyebrow={isKo ? "네트워크" : "Network"}
          title={translations.title}
          sub={translations.description}
        />

        {/* category filters */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mt-10 flex flex-wrap items-center justify-center gap-2"
        >
          {filters.map((item) => {
            const active = filter === item.key;
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => {
                  setFilter(item.key);
                  setExpanded(false);
                }}
                className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-all duration-200 ${
                  active
                    ? "border-primary bg-primary text-white shadow-sm shadow-primary/25"
                    : "border-slate-200 bg-white text-slate-600 hover:border-primary/30 hover:text-primary"
                }`}
              >
                {item.label}
                <span className={`font-mono text-xs ${active ? "text-white/70" : "text-slate-400"}`}>{item.count}</span>
              </button>
            );
          })}
        </motion.div>

        {/* logo wall: hairline grid */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="mt-10"
        >
          {isLoading ? (
            <div className="grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-slate-200 bg-slate-200/70 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
              {Array.from({ length: 12 }).map((_, index) => (
                <div key={index} className="h-24 animate-pulse bg-white" />
              ))}
            </div>
          ) : (
            <motion.div
              layout
              className="grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-slate-200 bg-slate-200/70 shadow-sm sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6"
            >
              <AnimatePresence mode="popLayout">
                {visible.map((logo) => (
                  <motion.div
                    layout
                    key={logo.id}
                    initial={{ opacity: 0, scale: 0.92 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.92 }}
                    transition={{ duration: 0.3 }}
                    className="group relative flex h-24 items-center justify-center bg-white p-5"
                  >
                    <img
                      src={getOptimizedImageUrl(logo.logo_url, { width: 200, quality: 85 })}
                      alt={`${logo.name} logo`}
                      className="max-h-11 max-w-[80%] object-contain opacity-75 grayscale transition-all duration-300 group-hover:opacity-100 group-hover:grayscale-0"
                      loading="lazy"
                      decoding="async"
                    />
                    <span className="pointer-events-none absolute inset-x-2 bottom-1.5 truncate text-center text-[10px] font-medium text-slate-500 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                      {logo.name}
                      {filter === "all" && <span className="text-slate-400"> · {categoryLabel[logo.category]}</span>}
                    </span>
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>
          )}
        </motion.div>

        {/* view all / show less */}
        {!isLoading && hasMore && (
          <div className="mt-8 text-center">
            <button
              type="button"
              onClick={() => setExpanded((value) => !value)}
              className="group inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-6 py-3 text-sm font-semibold text-slate-700 transition-all hover:border-primary/30 hover:text-primary"
            >
              {expanded
                ? translations.showLess
                : isKo
                  ? `전체 ${filtered.length}개 기관 모두 보기`
                  : `View all ${filtered.length} organizations`}
              <ChevronDown
                className={`h-4 w-4 transition-transform duration-300 ${expanded ? "rotate-180" : ""}`}
              />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default DemoLogos;
